import {ArrayHelper} from "./arrayHelper";
import {StringHelper} from "./stringHelper";
import {ObjectHelper} from "./objectHelper";
import {Hash} from "~/interfaces/hashInterfaces";
import {NumberHelper} from "~/helpers/numberHelper";

export let InstanceHelper = {
  isPresent(instance: any): boolean {
    if (this.isNil(instance)) {
      return false
    } else if (InstanceHelper.isArray(instance)) {
      return ArrayHelper.isPresent(instance);
    } else if (instance instanceof Date) {
      return true
    } else if (InstanceHelper.isObject(instance)) {
      return Object.keys(instance).length > 0;
    } else if (InstanceHelper.isBoolean(instance)) {
      return instance;
    } else if (InstanceHelper.isString(instance)) {
      return StringHelper.isPresent(instance);
    } else if (InstanceHelper.isNumber(instance)) {
      return NumberHelper.isPresent(instance);
    } else {
      return false;
    }
  },
  presence<T>(instance: T, fallback: unknown = null): unknown {
    return InstanceHelper.isPresent(instance) ? instance : fallback
  },
  isBlank(instance: any): boolean {
    return !InstanceHelper.isPresent(instance)
  },
  toArray<T>(instance: T | T[]): T[] {
    if (InstanceHelper.isPresent(instance)) {
      return (InstanceHelper.isArray(instance)) ? instance : [instance]
    } else return []
  },
  clone<T, U>(instance: string | number | T[] | Hash<U>): string | number | T[] | Hash<U> {
    if (InstanceHelper.isArray(instance)) {
      return ArrayHelper.clone(instance);
    } else if (InstanceHelper.isObject(instance)) {
      return ObjectHelper.clone(instance as Hash<U>);
    } else {
      return instance;
    }
  },
  isNil(instance: any): instance is null | undefined {
    return instance === null || instance === undefined
  },
  isObject(instance: any): instance is object {
    return typeof instance === 'object';
  },
  isArray(instance: any): instance is Array<any> {
    return Array.isArray(instance);
  },
  isBoolean(instance: any): instance is boolean {
    return typeof instance === 'boolean';
  },
  isString(instance: any): instance is string {
    return typeof instance === 'string';
  },
  isNumber(instance: any): instance is number {
    return typeof instance === 'number';
  },
  isFunction(instance: any): instance is Function {
    return typeof instance === 'function';
  },
  /**
   * Operate on a "single" instance or a collection, yielding once for each instance
   * @param value [Array<Object>, Object] essentially anything
   * @param callback function to call on single value or each value in array
   */
  actOn<T = any>(value: T | Array<T>, callback: (x) => any): any {
    if (Array.isArray(value)) {
      return value.map(callback)
    } else {
      return callback(value)
    }
  },
  /**
   * A place to normalize testing equality.
   * @example Date comparisons
   *   new Date('2022-10-20') === new Date('2022-10-20') // => false
   *   new Date('2022-10-20').toISOString() === new Date('2022-10-20').toISOString() // => true
   */
  isEqual(a: any, b: any): boolean {
    if (a instanceof Date && b instanceof Date) return a.toISOString() === b.toISOString()

    return a === b
  },
  toNumber(text: string | number, kind: 'integer' | 'float' = 'integer'): number {
    if (InstanceHelper.isString(text)) {
      const func = kind === 'integer' ? parseInt : parseFloat
      return func(text.replace(/,/g, ''), 10)
    } else if (InstanceHelper.isNumber(text)) {
      return text
    }
  },
  toInt(text: string): number {
    return InstanceHelper.toNumber(text, 'integer')
  },
  toFloat(text: string): number {
    return InstanceHelper.toNumber(text, 'float')
  },
};
